import { useState, useEffect, useMemo } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { RunWithDetails } from '../../types'
import { generateFullPlanPDF, generateCompactCardPDF } from '../../lib/pdf-export/generators'
import { Button } from '../ui/button'
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2 } from 'lucide-react'

// Set up PDF.js worker - use local copy for better reliability
pdfjs.GlobalWorkerOptions.workerSrc = `/pdf-js/pdf.worker.min.js`

interface ExportPreviewProps {
  run: RunWithDetails
  format: 'full' | 'compact'
}

export function ExportPreview({ run, format }: ExportPreviewProps) {
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [scale, setScale] = useState(1.0)

  useEffect(() => {
    let isMounted = true
    
    const generatePDF = async () => {
      setIsLoading(true)
      setError(null)
      
      try {
        const blob = format === 'full' 
          ? await generateFullPlanPDF(run)
          : await generateCompactCardPDF(run)
        
        if (isMounted) {
          setPdfBlob(blob)
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to generate PDF preview')
          console.error('PDF Preview Error:', err)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }
    
    generatePDF()
    
    return () => {
      isMounted = false
    }
  }, [run, format])

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setPageNumber(1)
  }

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(1, prev - 1))
  }

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(numPages, prev + 1))
  }

  const zoomIn = () => {
    setScale(prev => Math.min(2.0, prev + 0.2))
  }

  const zoomOut = () => {
    setScale(prev => Math.max(0.5, prev - 0.2))
  }

  // Memoize the file object to prevent unnecessary reloads
  const fileObject = useMemo(() => {
    if (!pdfBlob) return null
    // Use the blob directly as the file - react-pdf accepts Blob objects
    return pdfBlob
  }, [pdfBlob])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-primary-900">
            {format === 'full' ? 'Full Plan PDF Preview' : 'Compact Pace Card PDF Preview'}
          </h3>
        </div>

        <div className="border border-neutral-200 rounded-xl overflow-hidden bg-neutral-50 flex items-center justify-center h-96 shadow-minimal">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            <p className="text-sm text-neutral-600 font-medium">Generating PDF preview...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-primary-900">
            {format === 'full' ? 'Full Plan PDF Preview' : 'Compact Pace Card PDF Preview'}
          </h3>
        </div>

        <div className="border border-accent-200 rounded-xl overflow-hidden bg-accent-50 flex items-center justify-center h-96 shadow-minimal">
          <div className="text-center text-accent-700">
            <p className="font-semibold">Failed to generate PDF preview</p>
            <p className="text-sm mt-2 text-accent-600">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-primary-900">
          {format === 'full' ? 'Full Plan PDF Preview' : 'Compact Pace Card PDF Preview'}
        </h3>
        <div className="text-sm text-neutral-600 font-medium px-3 py-1 bg-neutral-100 rounded-xl">
          {format === 'full' ? 'A4 format with detailed information' : 'Single page race day card'}
        </div>
      </div>
      
      {/* PDF Controls */}
      <div className="flex items-center justify-between bg-neutral-50 px-4 py-3 rounded-xl border border-neutral-200">
        <div className="flex items-center gap-2">
          <Button 
            onClick={goToPrevPage} 
            disabled={pageNumber <= 1}
            variant="outline" 
            size="sm"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-semibold text-primary-900 px-3 py-1 bg-white rounded-lg border border-neutral-200">
            {pageNumber} of {numPages}
          </span>
          <Button 
            onClick={goToNextPage} 
            disabled={pageNumber >= numPages}
            variant="outline" 
            size="sm"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button onClick={zoomOut} disabled={scale <= 0.5} variant="outline" size="sm">
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm font-semibold text-primary-900 px-3 py-1 bg-white rounded-lg border border-neutral-200 min-w-[4rem] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button onClick={zoomIn} disabled={scale >= 2.0} variant="outline" size="sm">
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* PDF Viewer */}
      <div className="border border-neutral-200 rounded-xl overflow-hidden bg-neutral-50 shadow-minimal">
        <div className="flex justify-center p-6 min-h-[600px]">
          {fileObject && (
            <Document
              file={fileObject}
              onLoadSuccess={handleDocumentLoadSuccess}
              onLoadError={(error) => {
                console.error('PDF Load Error:', error)
                setError('Failed to load PDF preview')
              }}
              loading={
                <div className="flex items-center justify-center h-96">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          )}
        </div>
      </div>
      
      <div className="text-sm text-neutral-600 space-y-2 p-4 bg-neutral-50 rounded-xl border border-neutral-200">
        <p className="font-medium">
          💡 This is the exact PDF that will be downloaded or printed.
        </p>
        {format === 'compact' && (
          <p className="text-sm">
            The pace card is optimized for A4 printing and designed to be weather-resistant when laminated.
          </p>
        )}
      </div>
    </div>
  )
}